import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Card } from "@/design-system";
import logoWordmarkLight from "@/design-system/assets/logo-wordmark-light.svg";
import { useMyCredits } from "@/api/queries";
import { useAuth } from "@/lib/auth";
import { formatShortDate } from "@/lib/format";
import styles from "./MembershipCard.module.css";

/** Tracked uppercase micro-label, reversed on the gradient. */
const OVERLINE: CSSProperties = {
  font: "var(--fw-semibold) var(--fs-2xs)/1 var(--font-body)",
  letterSpacing: "var(--ls-wide)",
  textTransform: "uppercase",
  color: "var(--text-on-brand)",
  opacity: 0.78,
};

/**
 * MembershipCard — the student's entitlement rendered as a physical,
 * credit-card-shaped surface (issue #283): ISO/IEC 7810 ID-1
 * proportions (1.586:1) with the brand wordmark, an embossed mono
 * credit counter, and the cardholder + validity row along the bottom.
 *
 * The ID-1 ratio is held as a *minimum* rather than a fixed height: on a
 * 320px phone the content is taller than the ratio allows, so the card
 * grows instead of clipping its own bottom row (see the module CSS).
 *
 * Status lives in the validity slot at the bottom right. The wordmark and
 * a status pill do not fit side by side there — the card is only ~240px
 * wide on a 320px phone, and the wordmark alone is 140px — so the slot
 * shows the end date when the student is a member and a pill when they
 * are not.
 *
 * Reads only the student projection (`GET /api/me/credits` + the
 * `membership` / `credit_balance` on `/me`). No ledger provenance —
 * kind, note, author — ever reaches this surface.
 */
export function MembershipCard() {
  const { t } = useTranslation();
  const { user, token } = useAuth();

  const creditsQuery = useMyCredits(token);
  const balance = creditsQuery.data?.balance ?? user?.credit_balance ?? 0;
  const expiring = creditsQuery.data?.expiring ?? [];
  const membership = user?.membership ?? null;
  const memberName = user?.name || user?.email || "—";

  return (
    <div className={styles.frame}>
      <Card
        data-testid="membership-card"
        className={styles.card}
        padding="var(--space-4) var(--space-5)"
        style={{
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          background:
            "linear-gradient(135deg, var(--terracotta-700) 0%, var(--terracotta-500) 55%, var(--terracotta-600) 100%)",
          border: "1px solid var(--terracotta-700)",
          borderRadius: "var(--radius-xl)",
          boxShadow:
            "var(--shadow-brand), inset 0 1px 0 rgba(251, 238, 230, 0.25)",
          color: "var(--text-on-brand)",
        }}
      >
        {/* soft light sheen across the top-right corner */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(120% 85% at 100% 0%, rgba(251, 238, 230, 0.22), transparent 55%)",
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            position: "relative",
            zIndex: 1,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: "var(--space-2)",
          }}
        >
          {/* issuer wordmark */}
          <div>
            <img
              src={logoWordmarkLight}
              alt="TheCore Studio"
              style={{ height: 32, width: "auto", display: "block" }}
            />
          </div>

          {/* embossed credit counter */}
          <div>
            <div style={OVERLINE}>{t("profile.creditsLabel")}</div>
            <div
              data-testid="membership-credit-balance"
              style={{
                marginTop: 6,
                fontFamily: "var(--font-mono)",
                fontWeight: "var(--fw-bold)",
                fontSize: "clamp(1.75rem, 9vw, 3rem)",
                lineHeight: 1,
                letterSpacing: "0.04em",
                color: "var(--text-on-brand)",
                textShadow:
                  "0 1px 0 rgba(251, 238, 230, 0.35), 0 -1px 1px rgba(46, 40, 34, 0.25)",
              }}
            >
              {balance}
            </div>
            <div
              style={{
                marginTop: 8,
                maxWidth: "32ch",
                font: "var(--fw-regular) var(--fs-2xs)/1.35 var(--font-body)",
                color: "var(--text-on-brand)",
                opacity: 0.72,
              }}
            >
              {t("membership.creditsHint")}
            </div>
            {expiring.length > 0 && (
              <div
                data-testid="membership-expiring"
                style={{
                  marginTop: 6,
                  maxWidth: "32ch",
                  font: "var(--fw-semibold) var(--fs-2xs)/1.35 var(--font-body)",
                  color: "var(--text-on-brand)",
                  opacity: 0.9,
                }}
              >
                {t("membership.expiringLine", {
                  count: expiring[0].amount,
                  date: formatShortDate(expiring[0].expires_on),
                })}
              </div>
            )}
          </div>

          {/* cardholder + status — the bottom strip of a real card */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={OVERLINE}>{t("profile.member")}</div>
              <div
                style={{
                  marginTop: 5,
                  font: "var(--fw-semibold) var(--fs-sm)/1.2 var(--font-body)",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "var(--text-on-brand)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {memberName}
              </div>
            </div>
            <div
              data-testid="membership-status"
              style={{ textAlign: "right", flexShrink: 0 }}
            >
              {membership ? (
                <>
                  <div style={OVERLINE}>{t("membership.validThru")}</div>
                  <div
                    data-testid="membership-end-date"
                    style={{
                      marginTop: 5,
                      font: "var(--fw-bold) var(--fs-md)/1 var(--font-mono)",
                      color: "var(--text-on-brand)",
                    }}
                  >
                    {formatShortDate(membership.ends_on)}
                  </div>
                </>
              ) : (
                <Badge tone="brand" style={{ marginBottom: 5 }}>
                  {t("membership.inactiveBadge")}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
